import { useEffect, useState } from "react";
import { CheckCircle2, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const WHEEL_IMG = "/gallery/work-3.jpg";
const STORAGE_KEY = "bmd-subscribe-dismissed";

export const SubscribePopup = () => {
  const [show, setShow] = useState(false);
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem(STORAGE_KEY)) return;
    const t = setTimeout(() => setShow(true), 4000);
    return () => clearTimeout(t);
  }, []);

  const close = () => {
    sessionStorage.setItem(STORAGE_KEY, "1");
    setShow(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/subscribe", { email, company });
      setDone(true);
      sessionStorage.setItem(STORAGE_KEY, "1");
      toast.success(data.message || "Subscribed!");
      setTimeout(() => setShow(false), 2500);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  return (
    <div data-testid="subscribe-popup" className="fixed inset-0 z-[100] flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={close} />
      <div className="relative w-full max-w-3xl bg-[#121216] border border-[#232329] grid grid-cols-1 md:grid-cols-2 overflow-hidden jpd-fade-up">
        <button
          data-testid="subscribe-close-button"
          onClick={close}
          aria-label="Close subscribe popup"
          className="absolute top-3 right-3 z-10 p-2 text-slate-400 hover:text-white transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="hidden md:block">
          <img src={WHEEL_IMG} alt="Wheels and tires dressed after a Brad's Mobile Detailing detail" className="w-full h-full object-cover min-h-[380px]" />
        </div>

        <div className="p-8 md:p-10 flex flex-col justify-center">
          {done ? (
            <div data-testid="subscribe-success" className="text-center">
              <CheckCircle2 className="mx-auto w-12 h-12 text-[#10B981]" />
              <h2 className="mt-4 font-display uppercase text-2xl tracking-wide text-white">You're in!</h2>
              <p className="mt-2 text-sm text-slate-400">Watch your inbox for updates and deals.</p>
            </div>
          ) : (
            <>
              <h2 className="font-display uppercase text-3xl md:text-4xl tracking-wide text-white leading-tight">
                Subscribe for the <span className="text-[#EA0A0B]">updates!</span>
              </h2>
              <p className="mt-3 text-sm text-slate-400 leading-relaxed">
                Detailing tips, seasonal offers and package deals — straight to your inbox. No spam,
                ever.
              </p>
              <form data-testid="subscribe-form" onSubmit={submit} className="mt-6 space-y-3">
                <input
                  data-testid="subscribe-email-input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email address"
                  className="jpd-input"
                />
                <input
                  type="text"
                  name="company"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  tabIndex={-1}
                  autoComplete="off"
                  className="jpd-honeypot"
                  aria-hidden="true"
                />
                <button
                  data-testid="subscribe-submit-button"
                  disabled={loading}
                  className="w-full bg-[#EA0A0B] hover:bg-[#C20A0B] disabled:opacity-60 text-white font-display uppercase tracking-[0.12em] text-sm py-4 transition-colors"
                >
                  {loading ? "Subscribing…" : "Subscribe"}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
